import { Controller } from "@hotwired/stimulus"

/**
 * Reusable infinite scroll controller using IntersectionObserver
 *
 * Usage:
 *   <div data-controller="infinite-scroll"
 *        data-infinite-scroll-url-value="/path/to/load_more"
 *        data-infinite-scroll-page-value="1"
 *        data-infinite-scroll-per-page-value="10"
 *        data-infinite-scroll-has-more-value="true">
 *
 *     <div data-infinite-scroll-target="items">
 *       <!-- Initial items rendered here -->
 *     </div>
 *
 *     <div data-infinite-scroll-target="sentinel"></div>
 *     <div data-infinite-scroll-target="loader" class="d-none">Loading...</div>
 *     <div data-infinite-scroll-target="empty" class="d-none">No more items</div>
 *   </div>
 */
export default class extends Controller {
  static targets = ["items", "sentinel", "loader", "empty"]

  static values = {
    url: String,
    page: { type: Number, default: 1 },
    perPage: { type: Number, default: 10 },
    hasMore: { type: Boolean, default: true },
    loading: { type: Boolean, default: false },
    threshold: { type: Number, default: 0.1 },
    rootMargin: { type: String, default: "100px" }
  }

  connect() {
    this.isDisconnected = false
    this.setupObserver()
    this.restoreScrollPosition()
    this.boundSaveScrollPosition = this.saveScrollPosition.bind(this)
    document.addEventListener('turbo:before-cache', this.boundSaveScrollPosition)
  }

  disconnect() {
    this.isDisconnected = true
    document.removeEventListener('turbo:before-cache', this.boundSaveScrollPosition)
    this.destroyObserver()
  }

  setupObserver() {
    if (!this.hasSentinelTarget) return

    // Find the scrollable parent container
    const scrollableParent = this.findScrollableParent(this.element)

    const options = {
      root: scrollableParent,
      rootMargin: this.rootMarginValue,
      threshold: this.thresholdValue
    }

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.loadingValue && this.hasMoreValue) {
          this.loadMore()
        }
      })
    }, options)

    this.observer.observe(this.sentinelTarget)
  }

  findScrollableParent(element) {
    let parent = element.parentElement
    while (parent) {
      const style = getComputedStyle(parent)
      if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
        return parent
      }
      parent = parent.parentElement
    }
    return null
  }

  destroyObserver() {
    if (this.observer) {
      this.observer.disconnect()
      this.observer = null
    }
  }

  async loadMore() {
    if (this.isDisconnected || this.loadingValue || !this.hasMoreValue) return

    this.loadingValue = true
    this.showLoader()

    try {
      const nextPage = this.pageValue + 1
      const url = this.buildUrl(nextPage)

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'text/html',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })

      if (!response.ok || this.isDisconnected) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const html = await response.text()

      if (this.isDisconnected) return

      // Parse response to check for metadata
      const data = this.parseResponse(html)

      if (data.items && data.items.trim()) {
        this.appendItems(data.items)
        this.pageValue = nextPage
      }

      this.hasMoreValue = data.hasMore

      if (!this.hasMoreValue) {
        this.showEmpty()
        this.destroyObserver()
      }
    } catch (error) {
      if (!this.isDisconnected) {
        console.error('Error loading more items:', error)
        this.dispatch("error", { detail: { error } })
      }
    } finally {
      this.loadingValue = false
      this.hideLoader()
    }
  }

  buildUrl(page) {
    const url = new URL(this.urlValue, window.location.origin)
    url.searchParams.set('page', page)
    url.searchParams.set('per_page', this.perPageValue)
    return url.toString()
  }

  parseResponse(html) {
    // Check if response contains metadata wrapper
    const parser = new DOMParser()
    const doc = parser.parseFromString(html, 'text/html')

    // Look for data attributes on wrapper element
    const wrapper = doc.querySelector('[data-has-more]')
    if (wrapper) {
      return {
        items: wrapper.innerHTML,
        hasMore: wrapper.dataset.hasMore === 'true'
      }
    }

    // Fallback: assume all HTML is items, check for empty content
    const hasContent = html.trim().length > 0
    return {
      items: html,
      hasMore: hasContent
    }
  }

  appendItems(html) {
    if (!this.hasItemsTarget) return

    // Insert before sentinel if it exists within items, otherwise append
    if (this.hasSentinelTarget && this.sentinelTarget.parentElement === this.itemsTarget) {
      this.sentinelTarget.insertAdjacentHTML('beforebegin', html)
    } else {
      this.itemsTarget.insertAdjacentHTML('beforeend', html)
    }

    // Dispatch event for any parent components that need to react
    this.dispatch("loaded", { detail: { page: this.pageValue } })
  }

  showLoader() {
    if (this.hasLoaderTarget) {
      this.loaderTarget.classList.remove('d-none')
    }
  }

  hideLoader() {
    if (this.hasLoaderTarget) {
      this.loaderTarget.classList.add('d-none')
    }
  }

  showEmpty() {
    if (this.hasEmptyTarget) {
      this.emptyTarget.classList.remove('d-none')
    }
    if (this.hasSentinelTarget) {
      this.sentinelTarget.classList.add('d-none')
    }
  }

  saveScrollPosition() {
    const scrollableParent = this.findScrollableParent(this.element)
    if (scrollableParent && this.hasUrlValue) {
      const key = `infinite-scroll-${this.urlValue}`
      sessionStorage.setItem(key, JSON.stringify({
        scrollTop: scrollableParent.scrollTop,
        page: this.pageValue
      }))
    }
  }

  restoreScrollPosition() {
    if (!this.hasUrlValue) return

    const key = `infinite-scroll-${this.urlValue}`
    const saved = sessionStorage.getItem(key)
    if (saved) {
      sessionStorage.removeItem(key)
      const { scrollTop, page } = JSON.parse(saved)
      // Only restore if page matches (DOM was restored from Turbo cache with all rows)
      if (this.pageValue === page) {
        const scrollableParent = this.findScrollableParent(this.element)
        if (scrollableParent) {
          requestAnimationFrame(() => {
            scrollableParent.scrollTop = scrollTop
          })
        }
      }
    }
  }

  // Public method to reset the controller (useful after filtering)
  reset() {
    this.pageValue = 1
    this.hasMoreValue = true
    this.loadingValue = false

    if (this.hasEmptyTarget) {
      this.emptyTarget.classList.add('d-none')
    }
    if (this.hasSentinelTarget) {
      this.sentinelTarget.classList.remove('d-none')
    }

    this.destroyObserver()
    this.setupObserver()
  }

  // Public method to manually trigger a load
  manualLoad() {
    this.loadMore()
  }
}
